import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { NotificationRulesService } from '../../../../services/notification-rules.service';
import { RoutingOverview, ALERT_TYPE_LABEL } from '../../../../types/notification-rules.types';
import { leaderRoutedTypes, joinList } from '../../../../components/notification-rules/routing-copy';
import { useNotificationRulesEnabled } from '../../../../components/notification-rules/use-notification-rules-enabled';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { GroupsTeamsService } from '../../../../services/groups-teams.service';
import { toast } from 'react-toastify';

interface GroupSnapshot {
    name: string;
    location: string;
    tags: string[];
    teamIds?: string[];
    settings: Record<string, any>;
}

interface AddGroupLeaderModalProps {
    open: boolean;
    onClose: () => void;
    groupId: string;
    groupName: string;
    currentLeaderIds: string[];
    groupSnapshot: GroupSnapshot;
    onAdded: () => void;
}

interface SystemUser {
    id: string;
    fullname: string;
    email?: string;
}

export const AddGroupLeaderModal: React.FC<AddGroupLeaderModalProps> = ({
    open,
    onClose,
    groupId,
    groupName,
    currentLeaderIds,
    groupSnapshot,
    onAdded,
}) => {
    const [filterText, setFilterText] = useState('');

    // What a leader of this team will actually receive, straight from the
    // Notification Rules, so an admin promoting someone can see it here.
    // Only for organizations on Notification Rules; for the rest the picker
    // reads as it did before v2.
    const rulesOn = useNotificationRulesEnabled();
    const [routing, setRouting] = useState<RoutingOverview | null>(null);
    useEffect(() => {
        if (!open || !rulesOn) return;
        let cancelled = false;
        NotificationRulesService.fetchOverview()
            .then((o) => { if (!cancelled) setRouting(o); })
            .catch(() => { /* the picker still works without the explanation */ });
        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [open, rulesOn]);

    const routedLabels = rulesOn && routing
        ? leaderRoutedTypes(routing.rules).map((r) => ALERT_TYPE_LABEL[r.alertType])
        : [];
    const unreachableIds = new Set((routing?.teamStructure.unreachableLeaders ?? []).map((l) => l.userId));

    const [allUsers, setAllUsers] = useState<SystemUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [adding, setAdding] = useState<string | null>(null);
    const [addedIds, setAddedIds] = useState<string[]>([]);
    const didAdd = addedIds.length > 0;
    const PAGE_SIZE = 50;

    const fetchLeaders = (query: string, skip = 0, append = false) => {
        if (skip === 0) setLoading(true); else setLoadingMore(true);
        GroupsTeamsService.searchSystemUsers(query, PAGE_SIZE, skip).then((users) => {
            setAllUsers((prev) => append ? [...prev, ...users] : users);
            setHasMore(users.length === PAGE_SIZE);
        }).catch(() => {
            if (skip === 0) toast.error('Failed to load users.');
        }).finally(() => { setLoading(false); setLoadingMore(false); });
    };

    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 100 && hasMore && !loadingMore && !loading) {
            fetchLeaders(filterText.trim(), allUsers.length, true);
        }
    };

    useEffect(() => {
        if (!open) return;
        setAddedIds([]);
        setHasMore(true);
        fetchLeaders('');
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => { setHasMore(true); fetchLeaders(filterText.trim()); }, 300);
        return () => clearTimeout(timer);
    }, [filterText, open]);

    const allLeaderIds = useMemo(
        () => Array.from(new Set([...currentLeaderIds, ...addedIds])),
        [currentLeaderIds, addedIds]
    );

    const filtered = allUsers;

    const handleClose = () => {
        setFilterText('');
        setAllUsers([]);
        if (didAdd) onAdded();
        onClose();
    };

    const handleAdd = async (user: SystemUser) => {
        setAdding(user.id);
        const newLeaderIds = [...allLeaderIds, user.id];
        await GroupsTeamsService.updateGroup(groupId, {
            name: groupSnapshot.name,
            location: groupSnapshot.location,
            tags: groupSnapshot.tags,
            teamIds: groupSnapshot.teamIds || [],
            settings: groupSnapshot.settings,
            leadersIds: newLeaderIds,
        }, (isValid, message) => {
            setAdding(null);
            if (isValid) {
                toast.success(`${user.fullname || 'Leader'} added as group leader.`);
                setAddedIds((prev) => [...prev, user.id]);
            } else {
                toast.error(message || 'Failed to add leader.');
            }
        });
    };

    const isAlreadyLeader = (userId: string) => allLeaderIds.includes(userId);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Add Leader to {groupName}</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mb: '12px' }}>
                    Select a system user to add as a leader. Leaders must have Admin, Super Admin, or Group Leader
                    roles.
                    {rulesOn && routing && (routedLabels.length
                        ? ` Leaders of this group receive ${joinList(routedLabels)} alerts for its POM Users, so pick someone who can be reached.`
                        : ' Leaders of this group do not currently receive any alerts; recipients are set in Settings, under Notifications.')}
                </Typography>

                <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter by name or email..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: GT_COLORS.textFaint, fontSize: '18px' }} />,
                    }}
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                />

                {loading && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                )}

                {!loading && filtered.length === 0 && (
                    <Typography sx={{ fontSize: '14px', color: GT_COLORS.textFaint, textAlign: 'center', py: 3 }}>
                        {filterText ? 'No matching users.' : 'No system users found.'}
                    </Typography>
                )}

                <Box sx={{ minHeight: 280, maxHeight: 320, overflow: 'auto' }} onScroll={handleScroll}>
                    {filtered.map((user) => {
                        const already = isAlreadyLeader(user.id);
                        const justAdded = addedIds.includes(user.id);
                        return (
                            <Box
                                key={user.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: '8px',
                                    px: '10px',
                                    borderRadius: '6px',
                                    '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                }}
                            >
                                <Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                        {user.fullname || '(No name)'}
                                    </Typography>
                                    {user.email && (
                                        <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>
                                            {user.email}
                                        </Typography>
                                    )}
                                    {routedLabels.length > 0 && unreachableIds.has(String(user.id)) && (
                                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.red, fontWeight: 500 }}>
                                            No email, text or call, so they would not be notified
                                        </Typography>
                                    )}
                                </Box>
                                {already ? (
                                    <Typography
                                        sx={{ fontSize: '12px', color: justAdded ? GT_COLORS.green : GT_COLORS.textFaint, fontStyle: 'italic' }}
                                    >
                                        {justAdded ? 'Added' : 'Already a leader'}
                                    </Typography>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleAdd(user)}
                                        disabled={adding === user.id}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '13px',
                                            borderRadius: '6px',
                                            color: GT_COLORS.primary,
                                            borderColor: GT_COLORS.primary,
                                            minWidth: 60,
                                        }}
                                    >
                                        {adding === user.id ? <CircularProgress size={14} /> : 'Add'}
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                    {loadingMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};
